import { useEffect, useState } from 'react';

import type {
    PendingSave,
    ServiceRecordDraft,
    UseReturnToServiceParams,
    UseReturnToServiceReturn
} from './types';

const useReturnToService = ({
    drone,
    isOpen,
    onSave,
    onSetAirworthy
}: UseReturnToServiceParams): UseReturnToServiceReturn => {
    const [pendingSave, setPendingSave] = useState<PendingSave | null>(null);

    // Reseed whenever the panel opens so a reopened panel starts on the form step.
    useEffect(() => {
        if (isOpen) {
            setPendingSave(null);
        }
    }, [isOpen]);

    // Ready drones save straight through; grounded/hold drones park the draft and
    // surface the return-to-service question first.
    const handleRequestSave = ({ draft }: { draft: ServiceRecordDraft }) => {
        if (!drone) {
            return;
        }

        if (drone.readiness === 'ready') {
            onSave({ draft, droneId: drone.id });

            return;
        }

        setPendingSave({ draft, readiness: drone.readiness });
    };

    const handleConfirmKeep = () => {
        if (!drone || !pendingSave) {
            return;
        }

        onSave({ draft: pendingSave.draft, droneId: drone.id });
    };

    // Save first, then flip readiness — the airworthy log should postdate the record.
    // Reuses the page-level setAirworthy so the who/when attribution stays in one place.
    const handleConfirmReturn = () => {
        if (!drone || !pendingSave) {
            return;
        }

        onSave({ draft: pendingSave.draft, droneId: drone.id });
        onSetAirworthy({ droneId: drone.id, status: 'ready' });
    };

    const handleCancelConfirm = () => setPendingSave(null);

    return {
        handleCancelConfirm,
        handleConfirmKeep,
        handleConfirmReturn,
        handleRequestSave,
        pendingReadiness: pendingSave?.readiness ?? null
    };
};

export default useReturnToService;
